import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { MdAdd } from 'react-icons/md';
import ScrollButton from '../../ScrollButton/ScrollButton';
import Icon from '../../Icons/Icon';

const SiteTags = ({ handleOpenDeleteModal, assignedTags, setShowTagModal }) => {
  const containerRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isDisabledLeftButton, setIsDisabledLeftButton] = useState(true);
  const [isDisabledRightButton, setIsDisabledRightButton] = useState(false);

  // add ref to the container to check if it is overflowing and need to show the arrows
  const container = containerRef?.current;

  // check if the container is overflowing with tags, if so - show scroll buttons
  useEffect(() => {
    if (!assignedTags) return;
    setIsOverflowing(container && container.scrollWidth > container.clientWidth);
  }, [assignedTags, container]);

  // allow to disable scroll button
  useEffect(() => {
    // handle the scroll event disable the arrow buttons
    const handleClick = () => {
      setIsDisabledLeftButton(container.scrollLeft === 0);
      // we need to subtract 1px in the scrollLeft to make sure the scroll is at the end, because the scrollLeft is not always the same as the scrollWidth-scrollLeft
      setIsDisabledRightButton(container.scrollLeft >= container.scrollWidth - container.clientWidth - 1);
    };

    // all below this lane will happen if container is overloading
    // add event listener to the scroll event if the container is present and is overflowing
    if (container && isOverflowing) {
      container.addEventListener('scroll', handleClick);
    }
    return () => {
      if (container && isOverflowing) {
        container.removeEventListener('scroll', handleClick);
        setIsDisabledLeftButton(true);
        setIsDisabledRightButton(false);
      }
    };
  }, [isOverflowing, container]);

  return (
    <div className="w-full flex flex-col gap-y-4">
      <div className="flex gap-x-2">
        <strong className="text-vamos-black font-sans text-base font-bold">Tags</strong>
        <button
          type="button"
          className="w-6 h-6 flex items-center justify-center bg-vamos-black text-vamos-white rounded-full border-none p-0 hover:bg-vamos-green1 focus:outline-none"
          onClick={setShowTagModal}
          aria-label="Add new tag"
        >
          <MdAdd size={16} />
        </button>
      </div>

      <div className="flex gap-2">
        {isOverflowing && (
          <ScrollButton
            onClick={() => containerRef.current?.scrollBy({ left: -container.clientWidth, behavior: 'smooth' })}
            direction="left"
            isDisabled={isDisabledLeftButton}
          />
        )}
        <div ref={containerRef} className="flex gap-2 overflow-x-auto scrollbar-hidden">
          {assignedTags?.map((tag) => (
            <div
              key={tag.name}
              className="flex items-center flex-shrink-0 gap-2.5 text-lg bg-vamos-blue1 text-vamos-black text-sm font-bold uppercase border rounded-md px-3 py-1.5 h-9 w-auto whitespace-nowrap"
            >
              <button
                type="button"
                onClick={() => {
                  window.location.href = `/tags/${tag.id}`;
                }}
                className="border-none bg-transparent p-0 uppercase"
              >
                {tag.name}
              </button>

              <button
                type="button"
                onClick={() => handleOpenDeleteModal(tag, tag.name)}
                aria-label={`Remove ${tag.name} tag`}
                className="border-none bg-transparent p-0"
              >
                <Icon name="close_tag" />
              </button>
            </div>
          ))}
        </div>
        {isOverflowing && (
          <ScrollButton
            onClick={() => containerRef.current?.scrollBy({ left: container.clientWidth, behavior: 'smooth' })}
            direction="right"
            isDisabled={isDisabledRightButton}
          />
        )}
      </div>
    </div>
  );
};

SiteTags.propTypes = {
  handleOpenDeleteModal: PropTypes.func.isRequired,
  assignedTags: PropTypes.array.isRequired,
  setShowTagModal: PropTypes.func.isRequired,
};

export default SiteTags;
